import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Plus, Search, Inbox, AlertTriangle, CalendarClock, UserX, Timer, Settings2, LifeBuoy,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { useLabels } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import {
  ticketsService, Ticket, TicketStats, TicketPriority, TicketStatus, SlaPolicy, CreateTicketInput,
} from '@/services/tickets.service';
import { contactsService } from '@/services/contacts.service';
import { settingsService } from '@/services/settings.service';
import {
  PRIORITY_META, STATUS_META, STATUS_ORDER, PRIORITY_ORDER, initialsFor, slaCountdownLabel,
  SLA_STATE_PILL, primaryClock, requesterLabel, formatDuration,
} from './ticketFormat';

type TeamMember = { userUid: string; firstName: string; lastName?: string; email: string };
type ContactOption = { contactUid: string; firstName: string; lastName?: string; email?: string; phone?: string };

const STATUS_TABS: Array<{ value: TicketStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_META[s].label })),
];

export default function TicketsList() {
  const t = useLabels();
  const { user, profile } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const nav = useNavigate();

  const [stats, setStats] = useState<TicketStats | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);
  const [team, setTeam] = useState<TeamMember[]>([]);

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<TicketStatus | ''>('');
  const [priority, setPriority] = useState<TicketPriority | ''>('');
  const [assignee, setAssignee] = useState<string>('');
  const [atRisk, setAtRisk] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 25;

  const [newOpen, setNewOpen] = useState(false);
  const [slaOpen, setSlaOpen] = useState(false);

  const refreshStats = () => { ticketsService.stats().then(setStats).catch(() => {}); };

  const refreshList = async () => {
    setLoading(true);
    setListError(false);
    try {
      const r = await ticketsService.list({
        q: q.trim() || undefined,
        status: status || undefined,
        priority: priority || undefined,
        assignee: assignee || undefined,
        slaState: atRisk ? 'at_risk' : undefined,
        page, limit,
      });
      setTickets(r.items);
      setTotal(r.total);
    } catch { setListError(true); } finally { setLoading(false); }
  };

  const filtersActive = !!q.trim() || !!status || !!priority || !!assignee || atRisk;
  const clearFilters = () => { setQ(''); setStatus(''); setPriority(''); setAssignee(''); setAtRisk(false); setPage(1); };

  useEffect(() => { refreshStats(); }, []);
  useEffect(() => {
    settingsService.listTeam()
      .then((r) => setTeam(r.map((u) => ({ userUid: u.userUid, firstName: u.firstName, lastName: u.lastName, email: u.email }))))
      .catch(() => {});
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshList(); }, [q, status, priority, assignee, atRisk, page]);

  const memberName = (uid?: string) => {
    if (!uid) return null;
    const m = team.find((x) => x.userUid === uid);
    return m ? [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email : null;
  };

  const kpis = [
    { key: 'open', title: 'Open', icon: <Inbox size={14} />, value: stats?.open, accent: 'text-primary', accentBg: 'bg-primary-soft' },
    { key: 'breached', title: 'Breached', icon: <AlertTriangle size={14} />, value: stats?.breached, accent: 'text-danger', accentBg: 'bg-danger-soft' },
    { key: 'dueToday', title: 'Due today', icon: <CalendarClock size={14} />, value: stats?.dueToday, accent: 'text-warning', accentBg: 'bg-warning-soft' },
    { key: 'unassigned', title: 'Unassigned', icon: <UserX size={14} />, value: stats?.unassigned, accent: 'text-text-muted', accentBg: 'bg-surface-alt' },
    {
      key: 'avgFirstResponse', title: 'Avg first response', icon: <Timer size={14} />,
      value: stats?.avgFirstResponseMinutes != null ? formatDuration(stats.avgFirstResponseMinutes * 60_000) : '—',
      accent: 'text-info', accentBg: 'bg-info-soft', raw: true,
    },
  ] as const;

  return (
    <>
      <Topbar
        title={t('tickets')}
        breadcrumb={`${t('operations')} / ${t('tickets')}`}
        description={loading ? 'Loading…' : `${total} ticket${total === 1 ? '' : 's'}`}
        right={
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="secondary" iconLeft={<Settings2 size={14} />} onClick={() => setSlaOpen(true)}>
                SLA policy
              </Button>
            )}
            <Button variant="primary" iconLeft={<Plus size={14} />} onClick={() => setNewOpen(true)} className="whitespace-nowrap">
              New ticket
            </Button>
          </div>
        }
      />

      <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-5">
        {/* KPI tiles */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          {kpis.map((c) => (
            <div key={c.key} className="flex flex-col gap-2 rounded-2xl border border-border bg-surface p-4 shadow-soft">
              <div className="flex items-center gap-2 text-xs font-medium text-text-muted">
                <span className={clsx('flex h-7 w-7 items-center justify-center rounded-lg', c.accentBg, c.accent)}>{c.icon}</span>
                {c.title}
              </div>
              <span className="font-display text-[22px] font-bold tracking-tight text-text-primary tabular-nums">
                {stats ? c.value : <span className="skeleton inline-block h-6 w-10 rounded" />}
              </span>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1 sm:flex-none sm:w-72">
              <Input
                iconLeft={<Search size={14} />}
                placeholder="Search # number, subject, requester…"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                className="!h-9 !text-xs"
              />
            </div>
            <Select value={priority} onChange={(e) => { setPriority(e.target.value as TicketPriority | ''); setPage(1); }} className="!h-9 !w-auto !text-xs">
              <option value="">All priorities</option>
              {PRIORITY_ORDER.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
            </Select>
            <Select value={assignee} onChange={(e) => { setAssignee(e.target.value); setPage(1); }} className="!h-9 !w-auto !text-xs">
              <option value="">Everyone</option>
              <option value="me">Mine{profile ? ` (${profile.firstName})` : ''}</option>
              <option value="unassigned">Unassigned</option>
              {team.map((m) => (
                <option key={m.userUid} value={m.userUid}>{[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}</option>
              ))}
            </Select>
            <button
              onClick={() => { setAtRisk((v) => !v); setPage(1); }}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition',
                atRisk ? 'border-warning bg-warning-soft text-warning' : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
              )}
              title="Show tickets at risk of, or already past, their SLA"
            >
              <AlertTriangle size={12} />
              SLA at risk
            </button>
          </div>

          <div className="flex gap-1.5 overflow-x-auto pb-0.5">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => { setStatus(tab.value); setPage(1); }}
                className={clsx(
                  'inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition',
                  status === tab.value
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {listError && !loading ? (
          <LoadErrorState what={`your ${t('tickets').toLowerCase()}`} onRetry={() => void refreshList()} />
        ) : loading && tickets.length === 0 ? (
          <Card padded={false} className="overflow-hidden">
            <div className="divide-y divide-border">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4">
                  <div className="skeleton h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-1.5"><div className="skeleton h-4 w-48 rounded" /><div className="skeleton h-3 w-28 rounded" /></div>
                  <div className="skeleton h-5 w-16 rounded-full" />
                  <div className="skeleton h-5 w-16 rounded-full" />
                </div>
              ))}
            </div>
          </Card>
        ) : tickets.length === 0 && filtersActive ? (
          <NoMatchesState onClear={clearFilters} />
        ) : tickets.length === 0 ? (
          <EmptyState
            icon={<LifeBuoy size={26} />}
            title={`No ${t('tickets').toLowerCase()} yet`}
            description={`Create your first ${t('tickets', 'singular').toLowerCase()} to start tracking support requests with SLA timers.`}
            action={<Button onClick={() => setNewOpen(true)} iconLeft={<Plus size={14} />}>New ticket</Button>}
          />
        ) : (
          <>
            {/* Desktop table */}
            <Card padded={false} className="hidden overflow-hidden xl:block">
              <div className="grid grid-cols-[70px_1fr_110px_110px_150px_120px_100px] gap-3 border-b border-border bg-surface-alt/40 px-5 py-2.5 text-2xs font-semibold uppercase tracking-wide text-text-muted">
                <span>#</span><span>Subject</span><span>Priority</span><span>Status</span><span>SLA</span><span>Assignee</span><span>Updated</span>
              </div>
              <div className="divide-y divide-border">
                {tickets.map((tk) => (
                  <div
                    key={tk.ticketUid}
                    onClick={() => nav(`/tickets/${tk.ticketUid}`)}
                    className="grid cursor-pointer grid-cols-[70px_1fr_110px_110px_150px_120px_100px] items-center gap-3 px-5 py-3 text-sm transition-colors hover:bg-surface-alt/60"
                  >
                    <span className="font-mono text-xs text-text-muted">#{tk.number}</span>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-text-primary">{tk.subject}</div>
                      <div className="truncate text-2xs text-text-muted">{requesterLabel(tk)}</div>
                    </div>
                    <PriorityPill priority={tk.priority} />
                    <StatusPillEl status={tk.status} />
                    <SlaCountdownChip ticket={tk} />
                    <AssigneeCell name={memberName(tk.assigneeUserUid)} />
                    <span className="text-2xs text-text-muted">{timeAgo(tk.updatedAt)}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Mobile cards */}
            <div className="space-y-2.5 xl:hidden">
              {tickets.map((tk) => (
                <div
                  key={tk.ticketUid}
                  onClick={() => nav(`/tickets/${tk.ticketUid}`)}
                  className="cursor-pointer rounded-2xl border border-border bg-surface p-4 shadow-soft"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-2xs font-mono text-text-muted">#{tk.number}</div>
                      <div className="truncate font-medium text-text-primary">{tk.subject}</div>
                      <div className="truncate text-2xs text-text-muted">{requesterLabel(tk)}</div>
                    </div>
                    <AssigneeCell name={memberName(tk.assigneeUserUid)} />
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <PriorityPill priority={tk.priority} />
                    <StatusPillEl status={tk.status} />
                    <SlaCountdownChip ticket={tk} />
                  </div>
                </div>
              ))}
            </div>

            {total > limit && (
              <div className="flex items-center justify-between text-xs text-text-muted">
                <span>Page {page} of {Math.max(1, Math.ceil(total / limit))}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                  <Button size="sm" variant="secondary" disabled={page * limit >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <NewTicketModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        team={team}
        onCreated={() => { setNewOpen(false); refreshList(); refreshStats(); }}
      />
      {isAdmin && <SlaPolicyModal open={slaOpen} onClose={() => setSlaOpen(false)} />}
    </>
  );
}

function PriorityPill({ priority }: { priority: TicketPriority }) {
  const meta = PRIORITY_META[priority];
  return (
    <span className={clsx('inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-0.5 text-2xs font-semibold', meta.pill)}>
      <span className={clsx('h-1.5 w-1.5 rounded-full', meta.dot)} />
      {meta.label}
    </span>
  );
}

function StatusPillEl({ status }: { status: TicketStatus }) {
  const meta = STATUS_META[status];
  return <span className={clsx('inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-2xs font-semibold', meta.pill)}>{meta.label}</span>;
}

function AssigneeCell({ name }: { name: string | null }) {
  if (!name) return <span className="text-2xs text-text-muted">Unassigned</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-2xs text-text-secondary">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-soft text-[9px] font-semibold text-primary">
        {initialsFor(name)}
      </span>
      <span className="truncate">{name}</span>
    </span>
  );
}

/** Isolated so only this cell re-renders on the tick, not the whole table. */
function SlaCountdownChip({ ticket }: { ticket: Ticket }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((v) => v + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  const clock = primaryClock(ticket);
  return (
    <span className={clsx('inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-2xs font-semibold whitespace-nowrap', SLA_STATE_PILL[clock.state])}>
      {slaCountdownLabel(clock, ticket.status)}
    </span>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  return `${formatDuration(diff)} ago`;
}

// ─── New ticket modal ───────────────────────────────────────────────────────

function NewTicketModal({
  open, onClose, team, onCreated,
}: { open: boolean; onClose: () => void; team: TeamMember[]; onCreated: () => void }) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [category, setCategory] = useState('');
  const [assigneeUserUid, setAssigneeUserUid] = useState('');
  const [requesterMode, setRequesterMode] = useState<'contact' | 'manual'>('contact');
  const [contactUid, setContactUid] = useState('');
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [requesterName, setRequesterName] = useState('');
  const [requesterEmail, setRequesterEmail] = useState('');
  const [requesterPhone, setRequesterPhone] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    contactsService.list({ limit: 200 })
      .then((r) => setContacts(r.items.map((c) => ({ contactUid: c.contactUid, firstName: c.firstName, lastName: c.lastName, email: c.email, phone: c.phone }))))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSubject(''); setDescription(''); setPriority('normal'); setCategory(''); setAssigneeUserUid('');
      setRequesterMode('contact'); setContactUid(''); setRequesterName(''); setRequesterEmail(''); setRequesterPhone('');
    }
  }, [open]);

  const save = async () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    setBusy(true);
    try {
      const payload: CreateTicketInput = {
        subject: subject.trim(),
        description: description.trim() || undefined,
        priority,
        category: category.trim() || undefined,
        assigneeUserUid: assigneeUserUid || undefined,
      };
      if (requesterMode === 'contact' && contactUid) {
        payload.contactUid = contactUid;
      } else {
        payload.requesterName = requesterName.trim() || undefined;
        payload.requesterEmail = requesterEmail.trim() || undefined;
        payload.requesterPhone = requesterPhone.trim() || undefined;
      }
      await ticketsService.create(payload);
      toast.success('Ticket created');
      onCreated();
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title="New ticket" widthClass="max-w-lg">
      <FormField label="Subject" required>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Cannot access dashboard" />
      </FormField>
      <FormField label="Description">
        <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's going on?" />
      </FormField>

      <div className="mb-2 flex gap-1 rounded-lg border border-border bg-surface-alt p-0.5">
        <button
          type="button"
          onClick={() => setRequesterMode('contact')}
          className={clsx('flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition', requesterMode === 'contact' ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted')}
        >
          Existing client
        </button>
        <button
          type="button"
          onClick={() => setRequesterMode('manual')}
          className={clsx('flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition', requesterMode === 'manual' ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted')}
        >
          Someone else
        </button>
      </div>

      {requesterMode === 'contact' ? (
        <FormField label="Client" hint={contacts.length ? undefined : 'No contacts yet'}>
          <Select value={contactUid} onChange={(e) => setContactUid(e.target.value)}>
            <option value="">— none —</option>
            {contacts.map((c) => (
              <option key={c.contactUid} value={c.contactUid}>
                {[c.firstName, c.lastName].filter(Boolean).join(' ')}{c.email ? ` (${c.email})` : ''}
              </option>
            ))}
          </Select>
        </FormField>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <FormField label="Name"><Input value={requesterName} onChange={(e) => setRequesterName(e.target.value)} /></FormField>
          <FormField label="Email"><Input type="email" value={requesterEmail} onChange={(e) => setRequesterEmail(e.target.value)} /></FormField>
          <FormField label="Phone"><Input value={requesterPhone} onChange={(e) => setRequesterPhone(e.target.value)} /></FormField>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <FormField label="Priority">
          <Select value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}>
            {PRIORITY_ORDER.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
          </Select>
        </FormField>
        <FormField label="Assignee">
          <Select value={assigneeUserUid} onChange={(e) => setAssigneeUserUid(e.target.value)}>
            <option value="">— unassigned —</option>
            {team.map((m) => <option key={m.userUid} value={m.userUid}>{[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}</option>)}
          </Select>
        </FormField>
        <FormField label="Category">
          <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Billing, Bug…" />
        </FormField>
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={busy} disabled={busy || !subject.trim()}>Create</Button>
      </div>
    </Modal>
  );
}

// ─── SLA policy modal ───────────────────────────────────────────────────────

const POLICY_ROWS: Array<{ key: keyof SlaPolicy; label: string }> = [
  { key: 'urgent', label: 'Urgent' }, { key: 'high', label: 'High' }, { key: 'normal', label: 'Normal' }, { key: 'low', label: 'Low' },
];

function SlaPolicyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [policy, setPolicy] = useState<SlaPolicy | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    ticketsService.getSlaPolicy().then((r) => setPolicy(r.slaPolicy)).catch(() => {});
  }, [open]);

  const set = (key: keyof SlaPolicy, field: 'firstResponseMinutes' | 'resolutionMinutes', value: number) => {
    setPolicy((p) => p ? { ...p, [key]: { ...p[key], [field]: value } } : p);
  };

  const save = async () => {
    if (!policy) return;
    setBusy(true);
    try {
      await ticketsService.updateSlaPolicy({ slaPolicy: policy });
      toast.success('SLA policy updated');
      onClose();
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title="SLA policy" widthClass="max-w-xl">
      {!policy ? (
        <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-12 rounded-lg" />)}</div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-[100px_1fr_1fr] gap-3 text-2xs font-semibold uppercase tracking-wide text-text-muted">
            <span>Priority</span><span>First response (min)</span><span>Resolution (min)</span>
          </div>
          {POLICY_ROWS.map((row) => (
            <div key={row.key} className="grid grid-cols-[100px_1fr_1fr] items-center gap-3">
              <PriorityPill priority={row.key} />
              <Input
                type="number" min={1}
                value={policy[row.key].firstResponseMinutes}
                onChange={(e) => set(row.key, 'firstResponseMinutes', Math.max(1, parseInt(e.target.value, 10) || 1))}
              />
              <Input
                type="number" min={1}
                value={policy[row.key].resolutionMinutes}
                onChange={(e) => set(row.key, 'resolutionMinutes', Math.max(1, parseInt(e.target.value, 10) || 1))}
              />
            </div>
          ))}
          <p className="text-2xs text-text-muted">Applies to new tickets, and to existing ones the next time their priority changes. Calendar time — business hours aren't factored in yet.</p>
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={busy} disabled={busy || !policy}>Save</Button>
      </div>
    </Modal>
  );
}
